import type { Booking } from '#lib/types/generated/index.js';
import {
	type Customer,
	type CustomerPackage,
	type CustomerPackageUsage,
	type Package,
	PaymentMethod,
	PaymentPosMethod,
	apiAppBusinessCustomerpackageConsume,
	apiAppBusinessCustomerpackageCreate,
	apiAppBusinessCustomerpackageDelete,
	apiAppBusinessCustomerpackageRecredit,
	apiAppBusinessCustomerpackageRemoveusage,
	apiAppBusinessCustomerpackageUpdate,
	apiAppBusinessPackageList,
	apiAppBusinessPackageUpsert
} from '#lib/types/generated/index.js';
import { applyOptimisticPackageRecredit, toSavePackageRequest } from '#lib/helper/package.js';
import { generateUUID } from '#lib/helper/uuid.js';
import { optimisticArchive, optimisticDelete, optimisticSave } from '#lib/stores/optimistic.js';
import { now } from '#lib/stores/time.js';
import { get, writable } from 'svelte/store';
import { addCustomerToStore } from './customer';

export const packageStore = writable<Package[]>([]);
export const customerPackageStore = writable<CustomerPackage[]>([]);

export function setCustomerPackages(packages: CustomerPackage[]): void {
	const customers: Customer[] = [];
	for (const cp of packages) {
		if (cp.customer) customers.push(cp.customer);
	}
	if (customers.length > 0) {
		addCustomerToStore(customers);
	}
	customerPackageStore.set(packages);
}

export function upsertCustomerPackage(cp: CustomerPackage): void {
	if (cp.customer) {
		addCustomerToStore(cp.customer);
	}
	customerPackageStore.update((packages) => {
		const index = packages.findIndex((p) => p.id.trim() === cp.id.trim());
		if (index !== -1) {
			packages[index] = cp;
			return [...packages];
		}
		return [...packages, cp];
	});
}

export function reload(): Promise<void> {
	return apiAppBusinessPackageList().then((res) => {
		packageStore.set(res.items);
	});
}

export function savePackage(pkg: Package): Promise<Package> {
	return optimisticSave(packageStore, pkg, () =>
		apiAppBusinessPackageUpsert(pkg.id, toSavePackageRequest(pkg))
	);
}

export function deletePackage(pkg: Package): Promise<void> {
	return optimisticArchive(packageStore, pkg, () =>
		apiAppBusinessPackageUpsert(pkg.id, toSavePackageRequest(pkg, true)).then(() => {})
	);
}

export async function consumeCustomerPackage(
	cp: CustomerPackage,
	usages: CustomerPackageUsage[],
	bookingId?: Booking['id']
): Promise<CustomerPackage> {
	const backup = get(customerPackageStore);
	const updatedCp: CustomerPackage = {
		...cp,
		compositions: cp.compositions.map((c) => {
			// Restore credits from existing usages for this booking context
			const existingForBooking = bookingId
				? c.usages.filter((u) => u.bookingId === bookingId)
				: [];
			const restored = existingForBooking.reduce((sum, u) => sum + u.quantity, 0);
			// Apply new usages
			const consumed = usages
				.filter((u) => u.compositionId === c.id)
				.reduce((sum, u) => sum + u.quantity, 0);
			const delta = consumed - restored;
			const newQty = c.quantity - delta;
			const deltaUsages: CustomerPackageUsage[] =
				bookingId && delta !== 0
					? [
							{
								id: generateUUID(),
								customerPackageId: cp.id,
								compositionId: c.id,
								quantity: delta,
								offerId:
									usages.find((u) => u.compositionId === c.id)?.offerId ?? null,
								guestPriceId:
									usages.find((u) => u.compositionId === c.id)?.guestPriceId ??
									null,
								bookingId,
								usedAt: get(now)
							}
						]
					: !bookingId
						? usages.filter((u) => u.compositionId === c.id)
						: [];
			const newUsages = [...c.usages, ...deltaUsages];
			return newQty !== c.quantity || newUsages.length !== c.usages.length
				? { ...c, quantity: newQty, usages: newUsages }
				: c;
		})
	};

	customerPackageStore.update((customerPackages) =>
		customerPackages.map((p) => (p.id === cp.id ? updatedCp : p))
	);

	try {
		const serverCp = await apiAppBusinessCustomerpackageConsume(cp.id, {
			bookingId: bookingId ?? null,
			usages
		});
		upsertCustomerPackage(serverCp);
		return serverCp;
	} catch (err) {
		customerPackageStore.set(backup);
		throw err;
	}
}

async function recreditPackageUsage(pkgId: string, usageId: string): Promise<void> {
	await apiAppBusinessCustomerpackageRemoveusage(pkgId, usageId);
	customerPackageStore.update((packages) =>
		packages.map((p) => {
			if (p.id === String(pkgId)) {
				return {
					...p,
					compositions: p.compositions.map((c) => ({
						...c,
						usages: c.usages.filter((u) => u.id !== String(usageId))
					}))
				};
			}
			return p;
		})
	);
}

export async function removeCustomerPackageCompositionUsage(
	usage: CustomerPackageUsage,
	cp: CustomerPackage
): Promise<CustomerPackage> {
	cp = {
		...cp,
		compositions: cp.compositions.map((c) => {
			return c.id !== usage.compositionId
				? c
				: {
						...c,
						quantity: c.quantity + usage.quantity,
						usages: c.usages.filter((u) => u.id !== usage.id)
					};
		})
	};

	await recreditPackageUsage(cp.id, usage.id);

	return cp;
}

export interface CreateCustomerPackagePayload extends Partial<CustomerPackage> {
	customerId: string;
	method?: PaymentMethod;
	posMethod?: PaymentPosMethod;
}

export async function createCustomerPackage(
	payload: CreateCustomerPackagePayload
): Promise<CustomerPackage> {
	const serverCp = await apiAppBusinessCustomerpackageCreate({
		customerId: payload.customerId,
		packageId: payload.packageId ?? null,
		id: payload.id ?? null,
		price: payload.price ?? null,
		expiresOn: payload.expiresOn
			? typeof payload.expiresOn === 'string'
				? payload.expiresOn
				: payload.expiresOn.toISODate()
			: null,
		method: payload.method ?? PaymentMethod.Pos,
		posMethod: payload.posMethod ?? null,
		compositions: (payload.compositions ?? []).map((comp) => ({
			id: comp.id,
			quantity: comp.quantity,
			initialQuantity:
				'initialQuantity' in comp && typeof comp.initialQuantity === 'number'
					? comp.initialQuantity
					: comp.quantity,
			details: comp.details.map((d) => ({
				id: d.id,
				activityId: d.activityId ?? null,
				offerId: d.offerId ?? null,
				guestPriceId: d.guestPriceId ?? null
			}))
		}))
	});

	upsertCustomerPackage(serverCp);
	return serverCp;
}

export function updateCustomerPackage(cp: CustomerPackage): Promise<CustomerPackage> {
	return optimisticSave(customerPackageStore, cp, () =>
		apiAppBusinessCustomerpackageUpdate(cp.id, {
			customerId: cp.customerId,
			expiresOn: cp.expiresOn
				? typeof cp.expiresOn === 'string'
					? cp.expiresOn
					: cp.expiresOn.toISODate()
				: null
		})
	);
}

export async function deleteOrCloseCustomerPackage(
	cp: CustomerPackage,
	options: {
		action: 'delete' | 'close';
		refundAmount?: number | null;
		refundMethod?: PaymentMethod | null;
		refundPosMethod?: PaymentPosMethod | null;
	}
): Promise<void> {
	if (options.action === 'delete') {
		return optimisticDelete(customerPackageStore, cp, () =>
			apiAppBusinessCustomerpackageDelete(cp.id, options)
		);
	}

	const backup = get(customerPackageStore);
	const updated: CustomerPackage = {
		...cp,
		expiresOn: get(now)
	};
	upsertCustomerPackage(updated);

	try {
		await apiAppBusinessCustomerpackageDelete(cp.id, options);
	} catch (err) {
		customerPackageStore.set(backup);
		throw err;
	}
}

export function customerPackageRecredit(
	cp: CustomerPackage,
	compositionId: number,
	quantity: number
): Promise<void> {
	const backup = get(customerPackageStore);
	const updatedCp = applyOptimisticPackageRecredit(cp, compositionId, quantity);
	customerPackageStore.update((packages) =>
		packages.map((p) => (p.id === cp.id ? updatedCp : p))
	);

	return apiAppBusinessCustomerpackageRecredit(cp.id, {
		compositionId,
		quantity
	})
		.then((res) => {
			upsertCustomerPackage(res);
		})
		.catch((err) => {
			customerPackageStore.set(backup);
			throw err;
		});
}
